import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, Leaf, Video, Phone, Clock, ShieldCheck, Award, Loader2, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCREDITATIONS = [
  'Member, National Institute of Medical Herbalists (NIMH)',
  'Senior Associate, Royal Society of Medicine',
  'Member, Association of Naturopathic Practitioners (ANP)',
  'Founder, Harrow Health Matters',
];
const FOCUS = ['Women\u2019s Health & Hormonal Balance', 'Preconception & Fertility', 'Postpartum & Paediatric Care (Eczema, Immunity)', 'Metabolic & Gut Wellbeing'];

export default function DiscoveryCall() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', method: 'video', preferred_date: '', preferred_time: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/discovery-call`, form);
      setDone(true);
    } catch {
      alert('Sorry, something went wrong. Please try again or email support@iiypv.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="discovery-call-page">
      <SEO title="Free Discovery Call with Medical Herbalist Mrs. Varsha Soneji | IIY Phytoventra"
        description="Book a free 10\u201315 minute Discovery Call with Medical Herbalist Mrs. Varsha Soneji (37+ years). Personalised botanical guidance and holistic lifestyle support."
        keywords="free medical herbalist consultation UK, book herbalist consultation online, medical herbalist Mrs Varsha Soneji, NIMH medical herbalist UK, free discovery call herbalist, botanical wellness consultation"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Service',
              name: 'Free Discovery Call with a Medical Herbalist',
              serviceType: 'Medical herbalist consultation',
              description: 'A free 10\u201315 minute Discovery Call with Medical Herbalist Mrs. Varsha Soneji for personalised botanical guidance and holistic lifestyle support.',
              provider: { '@type': 'Organization', name: 'IIY Phytoventra', url: 'https://iiypv.com/' },
              areaServed: { '@type': 'Country', name: 'United Kingdom' },
              url: 'https://iiypv.com/discovery-call',
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP', availability: 'https://schema.org/InStock' },
            },
            {
              '@type': 'FAQPage',
              mainEntity: [
                { '@type': 'Question', name: 'Is the Discovery Call really free?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. The 10\u201315 minute Discovery Call with Medical Herbalist Mrs. Varsha Soneji is completely free and carries no obligation.' } },
                { '@type': 'Question', name: 'Who will I speak to?', acceptedAnswer: { '@type': 'Answer', text: 'You speak directly with Mrs. Varsha Soneji, a Medical Herbalist and member of the National Institute of Medical Herbalists (NIMH) with over 37 years of experience.' } },
                { '@type': 'Question', name: 'How is the call held?', acceptedAnswer: { '@type': 'Answer', text: 'You can choose a video call (Zoom/Teams) or a phone call at a time that suits you, and you receive a confirmation with the details once your slot is confirmed.' } },
                { '@type': 'Question', name: 'What can the Discovery Call help with?', acceptedAnswer: { '@type': 'Answer', text: 'It offers personalised botanical guidance and holistic lifestyle support. It is not a medical consultation and does not diagnose, treat or replace advice from your GP.' } },
              ],
            },
          ],
        }} />

      {/* Hero */}
      <section className="relative bg-emerald-900 text-white pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute -top-20 -right-16 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-amber-300 mb-5">
            <Leaf className="w-4 h-4" /> Integrative Herbal Wellness
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading leading-tight mb-5">
            Take Control of Your Health Naturally
          </h1>
          <p className="text-lg md:text-xl text-emerald-100/90 mb-4">Book Your Free 10–15 Min Discovery Call</p>
          <p className="text-base text-emerald-100/80 max-w-2xl mx-auto mb-9">
            Speak directly with Medical Herbalist Mrs. Varsha Soneji to discover how tailored botanical medicine and holistic lifestyle guidance can support your health goals.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[{ i: CheckCircle, t: '100% Free \u00b7 No Obligation' }, { i: Clock, t: '10\u201315 Minute Video/Phone Consultation' }, { i: Leaf, t: 'Personalised Botanical Guidance' }].map((b) => (
              <span key={b.t} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-medium">
                <b.i className="w-4 h-4 text-amber-300" /> {b.t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-start">
        {/* Practitioner */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl shadow-soft border border-stone-200 overflow-hidden" data-testid="practitioner-card">
          <img src="/varsha-soneji.jpg" alt="Mrs. Varsha Soneji, Medical Herbalist" className="w-full h-72 object-cover object-top" />
          <div className="p-7">
            <h2 className="text-2xl font-heading font-bold text-emerald-950">Mrs. Varsha Soneji</h2>
            <p className="text-emerald-700 font-medium text-sm mb-4">Medical Herbalist · 37+ Years Experience</p>
            <p className="text-sm text-stone-600 leading-relaxed mb-5">
              Holding a degree in Herbal Medicine, Varsha blends Western Herbalism, Indian &amp; Ayurvedic Medicine, and Traditional Chinese Herbs to offer targeted, phytochemical-led botanical support tailored to each individual.
            </p>
            <div className="space-y-2 mb-5">
              {ACCREDITATIONS.map((a) => (
                <div key={a} className="flex items-start gap-2 text-sm text-stone-700"><Award className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />{a}</div>
              ))}
            </div>
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">Key Focus Areas</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {FOCUS.map((f) => <span key={f} className="text-xs bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-full px-3 py-1.5">{f}</span>)}
            </div>
          </div>
        </motion.div>

        {/* Booking form */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl shadow-soft border border-stone-200 p-7 md:p-9" id="book" data-testid="booking-card">
          {done ? (
            <div className="text-center py-10" data-testid="booking-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5"><CheckCircle className="w-8 h-8 text-emerald-600" /></div>
              <h3 className="text-2xl font-heading font-bold text-emerald-950 mb-2">Thank you!</h3>
              <p className="text-stone-600 max-w-sm mx-auto">Your request has been received. Our team will contact you shortly to confirm your 10–15 minute appointment.</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-heading font-bold text-emerald-950 mb-1">Request Your Free Discovery Call</h2>
              <p className="text-sm text-stone-500 mb-6">We'll email you to confirm a time that suits you.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
                <Field label="Full Name" required><input required value={form.full_name} onChange={set('full_name')} className={inputCls} data-testid="bf-name" /></Field>
                <Field label="Email Address" required><input type="email" required value={form.email} onChange={set('email')} className={inputCls} data-testid="bf-email" /></Field>
                <Field label="Phone / WhatsApp Number" required><input required value={form.phone} onChange={set('phone')} className={inputCls} data-testid="bf-phone" /></Field>
                <Field label="Preferred Consultation Method" required>
                  <div className="flex gap-3">
                    {[{ v: 'video', i: Video, t: 'Video (Zoom/Teams)' }, { v: 'phone', i: Phone, t: 'Phone Call' }].map((m) => (
                      <button type="button" key={m.v} onClick={() => setForm((s) => ({ ...s, method: m.v }))} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-medium transition-colors ${form.method === m.v ? 'border-emerald-600 bg-emerald-50 text-emerald-900' : 'border-stone-200 text-stone-600'}`} data-testid={`bf-method-${m.v}`}>
                        <m.i className="w-4 h-4" /> {m.t}
                      </button>
                    ))}
                  </div>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Preferred Date"><input type="date" value={form.preferred_date} onChange={set('preferred_date')} className={inputCls} data-testid="bf-date" /></Field>
                  <Field label="Preferred Time"><input type="time" value={form.preferred_time} onChange={set('preferred_time')} className={inputCls} data-testid="bf-time" /></Field>
                </div>
                <Field label="Primary Health Goals / Message (Optional)"><textarea rows="3" value={form.message} onChange={set('message')} className={inputCls} data-testid="bf-message" /></Field>
                <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-emerald-950 font-bold py-4 rounded-xl transition-colors disabled:opacity-60 shadow-lg" data-testid="bf-submit">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />} Request My Free Discovery Call
                </button>
                <p className="text-[11px] text-stone-400 text-center">Botanical guidance is not a substitute for medical advice. No medicinal claims are made.</p>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const inputCls = 'w-full border-2 border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 bg-white';
const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-emerald-950 mb-1.5">{label}{required && <span className="text-amber-600"> *</span>}</label>
    {children}
  </div>
);
